/**
 * Mock fare-calendar generator.
 * Produces fares for 7 days before and after a selected departure date (15 days total).
 */

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Generate a fare calendar centered on a departure date.
 * @param {Object} opts
 * @param {string} opts.departureDate ISO date (YYYY-MM-DD) at the center.
 * @param {number} [opts.basePrice] Anchor price for the selected day.
 * @param {string} [opts.currency]
 * @param {number} [opts.range] Days on each side (default 7).
 * @returns {import('../types').FareCalendarDay[]}
 */
export function generateFareCalendar({
  departureDate,
  basePrice = 52000,
  currency = "PKR",
  range = 7,
} = {}) {
  const center = departureDate ? new Date(`${departureDate}T00:00:00`) : new Date();
  const days = [];

  for (let offset = -range; offset <= range; offset++) {
    const d = new Date(center);
    d.setDate(center.getDate() + offset);
    const iso = d.toISOString().slice(0, 10);

    // Vary price around the base; weekends a bit pricier.
    const isWeekend = d.getDay() === 0 || d.getDay() === 5 || d.getDay() === 6;
    const jitter = randInt(-6000, 6000) + (isWeekend ? randInt(1000, 4000) : 0);
    const price = Math.max(15000, basePrice + jitter);

    days.push({
      date: iso,
      weekday: WEEKDAYS[d.getDay()],
      dayLabel: `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`,
      price,
      currency,
      isSelected: offset === 0,
    });
  }

  // Flag the cheapest day for UI highlighting.
  const cheapest = Math.min(...days.map((x) => x.price));
  days.forEach((x) => (x.isCheapest = x.price === cheapest));

  return days;
}

export default generateFareCalendar;
